import React from 'react';
import { Package, Truck, Home, DollarSign, Globe, Shield } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Helmet } from 'react-helmet-async';

const ServicesPage = () => {
  const services = [
    {
      icon: Package,
      title: "International Shipping",
      description: "Send parcels from Canada to India and 185+ countries worldwide with trusted carriers like DHL, FedEx, and UPS.",
      link: "/shipping-to-india-from-canada",
      features: ["Door-to-door delivery", "Real-time tracking", "Customs clearance support"]
    },
    {
      icon: Truck,
      title: "Express Courier Services",
      description: "Fast international courier services from Toronto, Brampton, and across Canada with delivery in 3-7 business days.",
      link: "/international-courier-toronto",
      features: ["Express delivery", "Priority handling", "Signature confirmation"]
    },
    {
      icon: Home,
      title: "Relocation Shipping",
      description: "Complete household and personal belongings shipping services for international relocations from Canada to India.",
      link: "/relocation-shipping-canada-to-india",
      features: ["Bulk discounts", "Packing guidance", "Transfer of Residence support"]
    },
    {
      icon: DollarSign,
      title: "Economy Shipping",
      description: "Budget-friendly shipping options with competitive rates from $7.99/kg for large shipments over 100kg.",
      link: "/cheap-shipping-canada-to-india",
      features: ["Lowest rates", "Flexible timelines", "Consolidation options"]
    },
    {
      icon: Globe,
      title: "Worldwide Shipping",
      description: "Ship to over 185 countries with comprehensive international coverage and reliable delivery networks.",
      link: "/",
      features: ["Global reach", "Multiple carriers", "Competitive pricing"]
    },
    {
      icon: Shield,
      title: "Customs Clearance",
      description: "Expert guidance on customs documentation, declarations, and clearance procedures for smooth international delivery.",
      link: "/customs-declaration",
      features: ["Documentation help", "Compliance guidance", "Duty information"]
    }
  ];

  return (
    <>
      <Helmet>
        <title>International Shipping Services Canada | DHL FedEx UPS Alternative | Shippzy</title>
        <meta name="description" content="Compare international shipping rates from Canada. DHL, FedEx, UPS alternatives. Express courier, cargo, relocation services. Door to door delivery. Save up to 75%." />
        <link rel="canonical" href="https://www.shippzy.ca/services" />
      </Helmet>

      <div className="min-h-screen bg-white pt-24 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Hero Section */}
          <div className="text-center mb-16">
            <div className="inline-block bg-gradient-to-r from-red-600 to-yellow-500 text-white px-4 py-2 rounded-full text-sm font-semibold mb-6">
              🌍 Complete Shipping Solutions
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Our Services
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive international shipping services from Canada to India and 185+ countries worldwide. We handle everything from small parcels to large relocation shipments.
            </p>
          </div>

          {/* Services Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {services.map((service, index) => {
              const IconComponent = service.icon;
              return (
                <Card key={index} className="hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                  <CardHeader>
                    <div className="w-14 h-14 bg-gradient-to-r from-red-600 to-yellow-500 rounded-lg flex items-center justify-center mb-4">
                      <IconComponent className="w-7 h-7 text-white" />
                    </div>
                    <CardTitle>{service.title}</CardTitle>
                    <CardDescription className="text-base">{service.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 mb-4">
                      {service.features.map((feature, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-sm text-gray-600">
                          <span className="text-green-500">✓</span>
                          {feature}
                        </div>
                      ))}
                    </div>
                    <Button
                      className="w-full bg-gradient-to-r from-red-600 to-yellow-500 hover:from-red-700 hover:to-yellow-600 text-white"
                      onClick={() => window.location.href = service.link}
                    >
                      Learn More
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Why Choose Shippzy */}
          <Card className="bg-gradient-to-r from-gray-50 to-gray-100 shadow-xl mb-8">
            <CardContent className="pt-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Why Choose Shippzy?</h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="text-center">
                  <div className="text-4xl font-bold text-red-600 mb-2">185+</div>
                  <p className="text-gray-700">Countries Served</p>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-bold text-red-600 mb-2">75%</div>
                  <p className="text-gray-700">Savings on Shipping</p>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-bold text-red-600 mb-2">24/7</div>
                  <p className="text-gray-700">Tracking Support</p>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-bold text-red-600 mb-2">100%</div>
                  <p className="text-gray-700">Reliable Delivery</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* CTA Section */}
          <div className="text-center bg-gradient-to-r from-red-600 to-yellow-500 rounded-2xl p-12 text-white">
            <h2 className="text-3xl font-bold mb-4">Ready to Ship?</h2>
            <p className="text-xl mb-6">Get an instant quote and start shipping today</p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Button
                className="bg-white text-red-600 hover:bg-gray-100 px-8 py-3 text-lg"
                onClick={() => window.location.href = '/shipping-cost-canada-to-india'}
              >
                Get Quote
              </Button>
              <Button
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-red-600 px-8 py-3 text-lg"
                onClick={() => window.location.href = '/contact'}
              >
                Contact Us
              </Button>
            </div>
          </div>

          <div className="text-center mt-8">
            <Button
              variant="outline"
              onClick={() => window.location.href = '/'}
              className="border-2 border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white"
            >
              Back to Home
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ServicesPage;
